import { SBCRewardType } from "@/api/privateRequests/sbcs/getSbcSets";
import { IconButton } from "@/design-system/IconButton/IconButton";
import { convertSBCPrice } from "@/functions/convertPlayerPrice";
import { extractRewardUrl } from "@/functions/extractRewardUrl";
import { cn } from "@/lib/utils";
import { Tier, tierSelector } from "@/redux/players/playersSlice";
import { useTranslation } from "@repo/shared/i18n";
import { Check } from "lucide-react";
import { useSelector } from "react-redux";

import { SBCSummary } from "./SBCSummary";
import { SBCTile } from "./SBCTile";

type Props = {
  name: string;
  /**
   * The card's own call to action, rendered in the foot of the tile. The whole
   * card is already a link, so this is an affordance rather than a second
   * target — and it gives the credit marker somewhere to sit.
   */
  solveAction?: React.ReactNode;
  imageURL: string;
  reward?: SBCRewardType;
  requirements: string[];
  /** The cheapest cart of base cards, shown beside the requirements. */
  suggestedSolution?: React.ReactNode;
  /** Put the cart first: a Streamline challenge is read from its cards. */
  leadWithSolution?: boolean;
  price: number;
  pointsRequired?: number;
  onComplete: (completed: boolean) => void;
  disableComplete?: boolean;
  isCompleted?: boolean;
  description?: string;
  isOnSolutionsPage?: boolean;
};

const SBC = ({
  name,
  solveAction,
  imageURL,
  reward,
  requirements,
  suggestedSolution,
  leadWithSolution,
  price,
  pointsRequired,
  onComplete,
  disableComplete,
  isCompleted = false,
  description,
  isOnSolutionsPage,
}: Props) => {
  const { t } = useTranslation("sbcs");

  const tier: Tier = useSelector(
    /* newDesign: the chip's Tier paints a ring + contrasting foreground, which
       only the new tier table carries. The legacy one colours text and fill the
       same, so the letter disappears into the pill. */
    tierSelector(
      reward?.metaRating?.metarating || 0,
      reward?.metaRating?.playerRoleId,
      true,
    ),
  );

  /* The same square the kebab sits in on the set card, so the two controls in
     the foot of a card are the same object. The tick alone carries it: the
     label said what the tooltip already says. */
  const sbcToggle = disableComplete ? null : (
    <IconButton
      icon={<Check className="size-4" />}
      aria-label={t("sbcCard.markAsComplete")}
      title={t("sbcCard.markAsComplete")}
      className={cn(
        "text-icon",
        isCompleted && "bg-neutral-600 text-foreground",
      )}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onComplete(!isCompleted);
      }}
    />
  );

  const viewProps = {
    name,
    imageURL,
    reward,
    rewardUrl: extractRewardUrl(reward),
    tier,
    description,
    requirements,
    suggestedSolution,
    leadWithSolution,
    formattedPrice: convertSBCPrice(price),
    pointsRequired,
    sbcToggle,
  };

  return isOnSolutionsPage ? (
    <SBCSummary {...viewProps} />
  ) : (
    <SBCTile
      {...viewProps}
      solveAction={solveAction}
      isCompleted={isCompleted}
    />
  );
};

export default SBC;
